<script setup lang="ts">
import type { Color } from '../../types/shared'
import type { LoaderVariant } from './types'

const VARIANTS: Record<LoaderVariant, LoaderVariant> = {
  circular: 'circular',
  linear: 'linear',
}

type Props = {
  variant?: LoaderVariant
  size?: number | string
  color?: Color
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'circular',
  size: 24,
  color: 'var(--ui-primary)',
})

const circularStyles = computed(() => {
  return {
    width: `${props.size}px`,
    height: `${props.size}px`,
  }
})

const linearStyles = computed(() => {
  return {
    color: props.color,
  }
})

const strokeWidth = computed(() => {
  if (Number(props.size) >= 50) return 1.5

  return 3.5
})
</script>

<template>
  <span class="app-loader">
    <span
      v-if="variant === VARIANTS.circular"
      data-test-id="loader-circular"
      class="app-loader__circular"
      :style="circularStyles"
    >
      <svg
        class="app-loader__svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 43.636 43.636"
      >
        <circle
          class="app-loader__circle"
          fill="transparent"
          cx="50%"
          cy="50%"
          r="20"
          :stroke-width="strokeWidth"
          stroke-dasharray="125"
          stroke-dashoffset="125"
          :stroke="color"
        />
      </svg>
    </span>

    <span
      v-if="variant === VARIANTS.linear"
      data-test-id="loader-linear"
      class="app-loader__linear"
      :style="linearStyles"
    />
  </span>
</template>

<style lang="scss" scoped>
.app-loader {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  &__svg {
    transition: all 0.2s ease-in-out;
    transform: rotate(-90deg);
    transform-origin: center center;
    animation: app-loader-circular-rotate 1.4s linear infinite;
  }

  &__circle {
    z-index: 2;
    stroke-dasharray: 25, 200;
    stroke-dashoffset: 0;
    stroke-linecap: round;
    transition: all 0.2s ease-in-out, stroke-width 0s;
    transform: rotate(-90deg);
    transform-origin: center center;
    animation: app-loader-circular-dash 1.4s ease-in-out infinite,
      app-loader-circular-rotate 1.4s linear infinite;
  }

  &__linear {
    --ui-loader-gradient: no-repeat linear-gradient(currentColor 0 0);
    --ui-loader-offsetX: 4px;

    position: absolute;
    top: calc(100% - 2px);
    left: var(--ui-loader-offsetX);
    z-index: 3;
    width: calc(100% - var(--ui-loader-offsetX) * 2);
    height: 2px;
    overflow: hidden;
    background: var(--ui-loader-gradient), var(--ui-loader-gradient),
      var(--ui-grey--hover);
    background-size: 60% 100%;
    border-radius: 16px;
    animation: app-loader-linear 3s infinite;
  }
}

@keyframes app-loader-circular-rotate {
  100% {
    transform: rotate(270deg);
  }
}

@keyframes app-loader-circular-dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 100, 200;
    stroke-dashoffset: -15px;
  }
  100% {
    stroke-dasharray: 100, 200;
    stroke-dashoffset: -124px;
  }
}

@keyframes app-loader-linear {
  0% {
    background-position: -150% 0, -150% 0;
  }
  66% {
    background-position: 250% 0, -150% 0;
  }
  100% {
    background-position: 250% 0, 250% 0;
  }
}
</style>
